import {
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useField } from "formik";
import { FormikCustomSelect } from "src/components/Inputs/CustomSelect";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import { useGlossary } from "src/hooks/useGlossary";
import { useFormTemplateContext } from "../formTemplateContext";
import { AcceptNewFormEntriesSection } from "./AcceptNewFormEntriesSection";
import {
  InformationPopover,
  InformationPopoverTypes,
} from "./InformationPopover";

export const Configuration: React.FC = () => {
  const [{ value: lotteryAndOffersEnabledValue }] = useField<boolean>(
    "lotteryAndOffersEnabled"
  );
  const [{ value: allowDeclineAcceptedOfferEnabled }] = useField<boolean>(
    "allowDeclineAcceptedOfferEnabled"
  );
  const [{ value: allowStaffSchoolSelectionOverrideValue }] = useField<boolean>(
    "allowStaffSchoolSelectionOverride"
  );
  const [{ value: staffOverrideReasonType }] = useField<string>(
    "staffOverrideReasonType"
  );
  const [
    { value: staffOverrideReasonOptions },
    reasonOptionsMeta,
    { setValue: setReasonOptions },
  ] = useField<string[]>("staffOverrideReasonOptions");
  const { formTemplate } = useFormTemplateContext();
  const { glossaryTransform } = useGlossary({
    enrollmentPeriodId: formTemplate.enrollment_period.id,
  });

  return (
    <Flex direction="column" gap={6}>
      <Heading fontSize="lg" color="gray.700">
        Configuration
      </Heading>

      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="allowFormDeletionByFamily"
          checked={[true, ""]}
          unchecked={[false, ""]}
          formControlProps={{ width: "fit-content" }}
        />
        <Flex direction="column" width="100%">
          <Text>Allow families to delete their forms</Text>
          <Text fontSize="sm">
            When enabled, families may delete forms that are In Progress or
            Submitted. Deleted forms' data is restored if they start the same
            form again.
          </Text>
        </Flex>
      </Flex>
      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="allowDeclineAcceptedOfferEnabled"
          checked={[true, ""]}
          unchecked={[false, ""]}
          formControlProps={{ width: "fit-content" }}
        />
        <Flex direction="column" width="100%">
          <Text>Allow declining Accepted offers</Text>
          <Text fontSize="sm">
            When enabled, families and staff will be able to decline Accepted
            offers.
          </Text>
          {allowDeclineAcceptedOfferEnabled && (
            <Flex mt={2}>
              <FormikCustomSelect
                name="allowDeclineAcceptedOffer"
                label="Select who can decline Accepted offers"
                renderItem={(item) => item.label}
                renderSelectedItem={(item) => item?.label ?? "Please select"}
                items={[
                  { key: "both", label: "Both family and staff" },
                  { key: "admin", label: "Staff only" },
                ]}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="allowStaffSchoolSelectionOverride"
          checked={[true, ""]}
          unchecked={[false, ""]}
          formControlProps={{ width: "fit-content" }}
        />
        <Flex direction="column" width="100%">
          <Text>
            {glossaryTransform("Allow staff School selection overrides")}
          </Text>
          <Text fontSize="sm">
            {glossaryTransform(
              "When enabled, staff will be able to see all schools for a student's selection and potentially override eligibility."
            )}
          </Text>
          {allowStaffSchoolSelectionOverrideValue && (
            <Flex direction="column" gap={3} mt={2}>
              <InputWithValidationControl
                id="staffOverrideEligibleLabel"
                name="staffOverrideEligibleLabel"
                label="“Eligible” label (optional)"
                inputProps={{ placeholder: "Eligible" }}
              />
              <InputWithValidationControl
                id="staffOverrideIneligibleLabel"
                name="staffOverrideIneligibleLabel"
                label="“Ineligible” label (optional)"
                inputProps={{ placeholder: "Ineligible" }}
              />
              <FormikCustomSelect
                name="staffOverrideReasonType"
                label="Override reason input"
                renderItem={(item) => item.label}
                renderSelectedItem={(item) => item?.label ?? "Please select"}
                items={[
                  { key: "freeText", label: "Free text" },
                  { key: "options", label: "Selectable options" },
                ]}
              />
              {staffOverrideReasonType === "options" && (
                <FormControl
                  isInvalid={typeof reasonOptionsMeta.error === "string"}
                >
                  <Flex direction="column" gap={1}>
                    <Text fontSize="sm">
                      Override reason options (one per line)
                    </Text>
                    <Textarea
                      placeholder={`Reason 1\nReason 2`}
                      value={staffOverrideReasonOptions.join("\n")}
                      onChange={(e) =>
                        setReasonOptions(e.target.value.split("\n"))
                      }
                    />
                    {typeof reasonOptionsMeta.error === "string" && (
                      <FormErrorMessage>
                        {reasonOptionsMeta.error}
                      </FormErrorMessage>
                    )}
                  </Flex>
                </FormControl>
              )}
              <Flex alignItems="center">
                <FormikSwitchInput<boolean>
                  label=""
                  name="staffOverrideReasonRequired"
                  checked={[false, ""]}
                  unchecked={[true, ""]}
                  formControlProps={{ width: "fit-content" }}
                />
                <Text fontSize="sm">Make override reason optional</Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      <AcceptNewFormEntriesSection />
      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="lotteryAndOffersEnabled"
          checked={[true, ""]}
          unchecked={[false, ""]}
          formControlProps={{ width: "fit-content" }}
        />
        <Flex direction="column" width="100%">
          <>
            <Flex gap={2} alignItems="center">
              <Text>Lottery & offers</Text>
              <InformationPopover
                id={InformationPopoverTypes.LotteryAndOffers}
              />
            </Flex>
            <Text fontSize="sm">
              With lottery & offers; Capacities and Lottery orders, along with
              new Automatic messages and Statuses can be configured
            </Text>
          </>

          {lotteryAndOffersEnabledValue && (
            <Flex mt={2}>
              <InputWithValidationControl
                id="offerStartAt"
                name="offerStartAt"
                inputProps={{ type: "datetime-local" }}
                label="Offer starts"
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      {lotteryAndOffersEnabledValue && (
        <Flex>
          <FormikSwitchInput<boolean>
            label=""
            name="showWaitlistPosition"
            checked={[true, ""]}
            unchecked={[false, ""]}
            formControlProps={{ width: "fit-content" }}
          />

          <Flex direction="column" width="100%">
            <Text>Show waitlist position to families</Text>
            <Text fontSize="sm">
              When enabled, waitlist position will be visible to families on
              their waitlisted sub-status.
            </Text>
          </Flex>
        </Flex>
      )}

      <Flex>
        <FormikSwitchInput<boolean>
          label=""
          name="schoolSpecificMessagesEnabled"
          checked={[true, ""]}
          unchecked={[false, ""]}
          formControlProps={{ width: "fit-content" }}
        />

        <Flex direction="column" width="100%">
          <Text>{glossaryTransform("School specific automatic messages")}</Text>
          <Text fontSize="sm">
            {glossaryTransform(
              "When enabled, each school can activate or deactivate messages and edit their contents."
            )}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};
